import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SmartChart } from '../../features/charts/SmartChart';
import { ExternalLink } from 'lucide-react';
import { TabbedPage } from '../../features/common/TabbedPage';
import { cattleRegisterApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { formatDate, formatNumber, today } from '../../utils/format';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSKPI } from '../../components/ILMSKPI';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSCardSkeleton } from '../../components/ILMSLoadingState';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import type { RegisterCard } from '../../types';

// The farm's "Cattle Register & Breeding" workbook (Design Format/Cattle-
// Register-Breeding.xlsx), sheet for sheet: same order, same cards, same
// column headings - so the book and the portal read as one format.
// Read-only: every row comes from the live records the rest of the portal
// keeps, and entry stays in those existing forms (linked on each sheet).

function SheetHeader({ sheet, title, description, editTo, editLabel }: {
  sheet: string; title: string; description: string; editTo?: string; editLabel?: string;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div className="min-w-0">
        <p className="text-label text-ink-400 uppercase tracking-wide">{sheet}</p>
        <h2 className="text-card-title text-ink-900">{title}</h2>
        <p className="text-secondary text-ink-500 mt-0.5">{description}</p>
      </div>
      {editTo && (
        <Link to={editTo} className="inline-flex items-center gap-1.5 text-secondary font-medium text-brand-green-700 hover:underline">
          Enter / edit in {editLabel} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

function Cards({ cards }: { cards: RegisterCard[] }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
      {cards.map((c) => <ILMSKPI key={c.label} label={c.label} value={c.value} helperText={c.note} />)}
    </div>
  );
}

function useSearch<T>(rows: T[] | undefined, keys: (row: T) => (string | null | undefined)[]) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!rows || !needle) return rows ?? [];
    return rows.filter((r) => keys(r).some((v) => v?.toLowerCase().includes(needle)));
  }, [rows, q, keys]);
  return { q, setQ, filtered };
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <input
      id="register-search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="h-10 w-full sm:w-72 rounded-md border border-ink-300/50 bg-white px-3 text-body mb-3 focus-visible:outline-2 focus-visible:outline-brand-green-500"
    />
  );
}

function Loading({ query }: { query: { isLoading: boolean; isError: boolean; error: unknown } }) {
  if (query.isLoading) return <ILMSCardSkeleton count={1} />;
  if (query.isError) return <ILMSAlert tone="critical">{getApiErrorMessage(query.error)}</ILMSAlert>;
  return null;
}

const dash = (v: string | number | null | undefined) => (v == null || v === '' ? '—' : v);
const tagKeys = (r: { tag_no?: string | null; name?: string | null }) => [r.tag_no, r.name];

// ---------------------------------------------------------------------------

function DashboardSheet() {
  const query = useQuery({ queryKey: ['register-dashboard'], queryFn: () => cattleRegisterApi.dashboard() });
  const d = query.data;
  return (
    <ILMSCard>
      <SheetHeader sheet="Dashboard" title="Herd Dashboard" description="Animals in milk and dry, pregnancies, calvings due, herd milk per day and the top yielders." />
      <Loading query={query} />
      {d && (
        <>
          <Cards cards={d.cards} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2">
              <div className="mb-2">
                <SmartChart id="register-herd-milk" data={d.herd_milk} />
              </div>
              <ILMSTable
                columns={[
                  { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.date) },
                  { key: 'am', header: 'AM (L)', align: 'right', render: (r) => formatNumber(r.am, 1) },
                  { key: 'am_cows', header: 'Cows AM', align: 'right', render: (r) => r.am_animals ?? '—' },
                  { key: 'pm', header: 'PM (L)', align: 'right', render: (r) => formatNumber(r.pm, 1) },
                  { key: 'pm_cows', header: 'Cows PM', align: 'right', render: (r) => r.pm_animals ?? '—' },
                  { key: 'total', header: 'Total (L)', align: 'right', render: (r) => formatNumber(r.total, 1) },
                  { key: 'animals', header: 'Cows milked (day)', align: 'right', render: (r) => r.animals },
                ]}
                data={d.herd_milk} rowKey={(r) => r.date} exportTitle="Herd Milk per Day" exportDateOf={(r) => r.date}
              />
            </div>
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-card-title mb-2">Calvings Due (soonest first)</p>
                <ILMSTable
                  columns={[
                    { key: 'expected', header: 'Expected', primary: true, render: (r) => `${formatDate(r.expected)} · ${r.days >= 0 ? `in ${r.days} d` : `overdue ${-r.days} d`}` },
                    { key: 'animal', header: 'Animal', render: (r) => dash(r.name) },
                    { key: 'tag', header: 'Tag', render: (r) => r.tag_no },
                  ]}
                  data={d.calvings_due} rowKey={(r) => `${r.tag_no}-${r.expected}`} emptyTitle="No calvings due in the next 60 days."
                  emptyDescription="To add one: Breeding → Pregnancy → Add pregnancy check — Result Positive, with the Expected Birth Date."
                />
              </div>
              <div>
                <p className="text-card-title mb-2">Top yielders (L a day)</p>
                <ILMSTable
                  columns={[
                    { key: 'tag', header: 'Tag', primary: true, render: (r) => r.tag_no },
                    { key: 'name', header: 'Name', render: (r) => dash(r.name) },
                    { key: 'avg', header: 'L / day', align: 'right', render: (r) => formatNumber(r.avg_milk_per_day, 1) },
                  ]}
                  data={d.top_yielders} rowKey={(r) => r.tag_no} emptyTitle="No milk recorded in the last 7 days."
                />
              </div>
              <div>
                <p className="text-card-title mb-2">Herd by status</p>
                <ILMSTable
                  columns={[
                    { key: 'status', header: 'Status', primary: true, render: (r) => r.status },
                    { key: 'count', header: 'Animals', align: 'right', render: (r) => r.count },
                  ]}
                  data={d.by_status} rowKey={(r) => r.status}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </ILMSCard>
  );
}

function AnimalRegisterSheet() {
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const query = useQuery({ queryKey: ['register-animals', includeDeparted], queryFn: () => cattleRegisterApi.animals(includeDeparted) });
  const { q, setQ, filtered } = useSearch(query.data?.rows, tagKeys);
  const columns: ILMSTableColumn<NonNullable<typeof query.data>['rows'][number]>[] = [
    { key: 'tag', header: 'Tag No.', primary: true, render: (r) => r.tag_no },
    { key: 'name', header: 'Name', render: (r) => dash(r.name) },
    { key: 'species', header: 'Species', render: (r) => dash(r.species) },
    { key: 'type', header: 'Animal Type', render: (r) => dash(r.animal_type), hideOnTablet: true },
    { key: 'born', header: 'Date In / Born', render: (r) => formatDate(r.date_in_born), hideOnTablet: true },
    { key: 'source', header: 'Source', render: (r) => r.source, hideOnTablet: true },
    { key: 'lactation', header: 'Lactation No.', align: 'right', render: (r) => dash(r.lactation_no) },
    { key: 'status', header: 'Status', render: (r) => r.status },
    { key: 'last_calving', header: 'Last Calving', render: (r) => formatDate(r.last_calving) },
    { key: 'dim', header: 'Days in Milk', align: 'right', render: (r) => dash(r.days_in_milk) },
    { key: 'breeding', header: 'Breeding', render: (r) => dash(r.breeding) },
    { key: 'expected', header: 'Expected Calving', render: (r) => formatDate(r.expected_calving) },
    { key: 'avg', header: 'Avg Milk / Day (L)', align: 'right', render: (r) => (r.avg_milk_per_day != null ? formatNumber(r.avg_milk_per_day, 1) : '—') },
  ];
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 2" title="Animal Register" description="Every animal once: tag, species, type, source, lactation, status, last calving and breeding stage."
        editTo="/animals" editLabel="Animals" />
      <Loading query={query} />
      {query.data && (
        <>
          <Cards cards={query.data.cards} />
          <div className="flex flex-wrap items-center gap-4">
            <SearchBox value={q} onChange={setQ} placeholder="Search tag or name…" />
            <label className="inline-flex items-center gap-2 text-secondary text-ink-600 mb-3" htmlFor="include-departed">
              <input id="include-departed" type="checkbox" checked={includeDeparted} onChange={(e) => setIncludeDeparted(e.target.checked)} />
              Include sold / dead / returned
            </label>
          </div>
          <ILMSTable columns={columns} data={filtered} rowKey={(r) => r.animal_id} emptyTitle="No animals match." exportTitle="Animal Register" />
        </>
      )}
    </ILMSCard>
  );
}

function BreedingSheet() {
  const query = useQuery({ queryKey: ['register-breeding'], queryFn: () => cattleRegisterApi.breeding() });
  const { q, setQ, filtered } = useSearch(query.data?.rows, tagKeys);
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 3" title="Breeding & AI Log" description="Heat, AI or natural service, repeat heat check, PD due, expected calving and dry-off date."
        editTo="/breeding" editLabel="Breeding" />
      <Loading query={query} />
      {query.data && (
        <>
          <Cards cards={query.data.cards} />
          <SearchBox value={q} onChange={setQ} placeholder="Search tag or name…" />
          <ILMSTable
            columns={[
              { key: 'tag', header: 'Tag No.', primary: true, render: (r) => r.tag_no },
              { key: 'name', header: 'Name', render: (r) => dash(r.name) },
              { key: 'species', header: 'Species', render: (r) => dash(r.species), hideOnTablet: true },
              { key: 'heat', header: 'Heat Date', render: (r) => formatDate(r.heat_date), hideOnTablet: true },
              { key: 'service', header: 'AI / Service Date', render: (r) => formatDate(r.service_date) },
              { key: 'type', header: 'AI / Natural', render: (r) => r.ai_natural },
              { key: 'bull', header: 'Bull / Semen ID', render: (r) => dash(r.bull_semen_id), hideOnTablet: true },
              { key: 'repeat', header: 'Repeat Heat Check', render: (r) => formatDate(r.repeat_heat_check) },
              { key: 'pd_due', header: 'PD Due', render: (r) => formatDate(r.pd_due) },
              { key: 'pd_result', header: 'PD Result', render: (r) => dash(r.pd_result) },
              { key: 'expected', header: 'Expected Calving', render: (r) => formatDate(r.expected_calving) },
              { key: 'dry_off', header: 'Dry-off Date', render: (r) => formatDate(r.dry_off_date), hideOnTablet: true },
              { key: 'stage', header: 'Stage', render: (r) => dash(r.stage) },
            ]}
            data={filtered} rowKey={(r) => r.breeding_record_id} emptyTitle="No services recorded yet."
            exportTitle="Breeding and AI Log" exportDateOf={(r) => r.service_date ?? r.heat_date}
          />
        </>
      )}
    </ILMSCard>
  );
}

function CalvingSheet() {
  const query = useQuery({ queryKey: ['register-calvings'], queryFn: () => cattleRegisterApi.calvings() });
  const { q, setQ, filtered } = useSearch(query.data?.rows, (r) => [r.mother_tag, r.mother, r.calf_tag]);
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 4" title="Calving Record" description="Every calving: date, mother, calf tag, sex and notes — from Breeding › Calving and Dairy › Calf Records."
        editTo="/breeding" editLabel="Breeding › Calving" />
      <Loading query={query} />
      {query.data && (
        <>
          <Cards cards={query.data.cards} />
          <SearchBox value={q} onChange={setQ} placeholder="Search mother or calf tag…" />
          <ILMSTable
            columns={[
              { key: 'date', header: 'Calving Date', primary: true, render: (r) => formatDate(r.calving_date) },
              { key: 'mother_tag', header: 'Mother Tag', render: (r) => r.mother_tag },
              { key: 'mother', header: 'Mother', render: (r) => dash(r.mother) },
              { key: 'species', header: 'Species', render: (r) => dash(r.species), hideOnTablet: true },
              { key: 'calf_tag', header: 'Calf Tag', render: (r) => dash(r.calf_tag) },
              { key: 'calf_sex', header: 'Calf Sex', render: (r) => dash(r.calf_sex) },
              { key: 'notes', header: 'Notes', render: (r) => dash(r.notes) },
              { key: 'from', header: 'Recorded In', render: (r) => r.source, hideOnTablet: true },
            ]}
            data={filtered} rowKey={(r) => `${r.calving_date}-${r.mother_tag}-${r.calf_tag ?? ''}-${r.source}`} emptyTitle="No calvings recorded yet."
            exportTitle="Calving Record" exportDateOf={(r) => r.calving_date}
          />
        </>
      )}
    </ILMSCard>
  );
}

function MilkSheet() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 9); return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(today());
  const query = useQuery({ queryKey: ['register-milk', startDate, endDate], queryFn: () => cattleRegisterApi.milk(startDate, endDate) });
  const { q, setQ, filtered } = useSearch(query.data?.rows, tagKeys);
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 5" title="Milk Yield Log" description="Morning and evening milk of each animal, day by day, with the total — approved milk sessions, batches split evenly."
        editTo="/dairy" editLabel="Dairy › Milk Sessions" />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <ILMSDatePicker label="From" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        <ILMSDatePicker label="To" value={endDate} max={today()} onChange={(e) => setEndDate(e.target.value)} />
      </div>
      <Loading query={query} />
      {query.data && (
        <>
          <Cards cards={query.data.cards} />
          <SearchBox value={q} onChange={setQ} placeholder="Search tag or name…" />
          <ILMSTable
            columns={[
              { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.date) },
              { key: 'tag', header: 'Tag No.', render: (r) => r.tag_no },
              { key: 'name', header: 'Name', render: (r) => dash(r.name) },
              { key: 'am', header: 'AM (L)', align: 'right', render: (r) => formatNumber(r.am, 2) },
              { key: 'pm', header: 'PM (L)', align: 'right', render: (r) => formatNumber(r.pm, 2) },
              { key: 'total', header: 'Total (L)', align: 'right', render: (r) => formatNumber(r.total, 2) },
            ]}
            data={filtered} rowKey={(r) => `${r.date}-${r.tag_no}`} emptyTitle="No approved milk in this range."
            exportTitle="Milk Yield Log" exportDateOf={(r) => r.date}
          />
        </>
      )}
    </ILMSCard>
  );
}

function VetSheet() {
  const query = useQuery({ queryKey: ['register-vet'], queryFn: () => cattleRegisterApi.vetVisits() });
  const { q, setQ, filtered } = useSearch(query.data?.rows, (r) => [r.tag_no, r.name, r.reason]);
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 6" title="Vet Visit Record" description="Date, animal, reason, cost and the next visit date your vet gives you: a record, not advice."
        editTo="/health" editLabel="Health › Vet Visits" />
      <Loading query={query} />
      {query.data && (
        <>
          <Cards cards={query.data.cards} />
          <SearchBox value={q} onChange={setQ} placeholder="Search tag, name or reason…" />
          <ILMSTable
            columns={[
              { key: 'date', header: 'Visit Date', primary: true, render: (r) => formatDate(r.visit_date) },
              { key: 'tag', header: 'Tag No.', render: (r) => dash(r.tag_no) },
              { key: 'name', header: 'Name', render: (r) => dash(r.name) },
              { key: 'reason', header: 'Reason for Visit', render: (r) => r.reason },
              { key: 'cost', header: 'Cost (₹)', align: 'right', render: (r) => (r.cost != null ? formatNumber(r.cost, 0) : '—') },
              { key: 'next', header: 'Next Visit (as given by your vet)', render: (r) => formatDate(r.next_visit) },
              { key: 'follow', header: 'Follow-up', render: (r) => (
                <span className={r.follow_up?.startsWith('Overdue') ? 'text-critical-700 font-medium' : r.follow_up === 'Visit done' ? 'text-success-700' : ''}>{dash(r.follow_up)}</span>
              ) },
            ]}
            data={filtered} rowKey={(r) => `${r.visit_date}-${r.tag_no ?? ''}-${r.reason}`} emptyTitle="No vet visits recorded yet."
            exportTitle="Vet Visit Record" exportDateOf={(r) => r.visit_date}
          />
        </>
      )}
    </ILMSCard>
  );
}

function SettingsSheet() {
  const query = useQuery({ queryKey: ['register-settings'], queryFn: () => cattleRegisterApi.settings() });
  const s = query.data;
  return (
    <ILMSCard>
      <SheetHeader sheet="Sheet 1 · Lists" title="Settings & Lists" description="As-on date, alert windows and the breeding days of each species: gestation, heat cycle, PD and dry-off."
        editTo="/assets" editLabel="Assets Registry › Animal Types" />
      <Loading query={query} />
      {s && (
        <div className="flex flex-col gap-5">
          <ILMSTable
            columns={[
              { key: 'item', header: 'Item', primary: true, render: (r) => r.item },
              { key: 'value', header: 'Value', render: (r) => r.value },
              { key: 'help', header: 'Help', render: (r) => r.help },
            ]}
            data={[
              { item: 'As-on date (today)', value: formatDate(s.as_on_date), help: 'Every date and count on these sheets is worked out as of today.' },
              { item: 'Calving alert, first window', value: `${s.calving_alert_first_days} days`, help: 'Calving Soon' },
              { item: 'Calving alert, second window', value: `${s.calving_alert_second_days} days`, help: 'Calving Later, and the Calvings Due list' },
              { item: 'Average milk over the last', value: `${s.average_milk_days} days`, help: 'Avg Milk / Day on the Animal Register and Top yielders' },
            ]}
            rowKey={(r) => r.item}
          />
          <div>
            <p className="text-card-title mb-1">Breeding Days by Species</p>
            <p className="text-helper-text text-ink-400 mb-2">
              Repeat heat check = AI date + heat cycle days. PD due = AI date + PD days. Expected calving = AI date + gestation days. Dry-off = expected calving − dry-off days.
            </p>
            <ILMSTable
              columns={[
                { key: 'species', header: 'Species', primary: true, render: (r) => r.species },
                { key: 'gestation', header: 'Gestation Days', align: 'right', render: (r) => r.gestation_days },
                { key: 'heat', header: 'Heat Cycle Days', align: 'right', render: (r) => r.heat_cycle_days },
                { key: 'pd', header: 'PD Check After AI (days)', align: 'right', render: (r) => r.pd_check_days },
                { key: 'dry', header: 'Dry-off Before Calving (days)', align: 'right', render: (r) => r.dry_off_days },
                { key: 'set', header: 'Set', render: (r) => (r.customised ? 'Custom' : 'Defaults') },
              ]}
              data={s.species} rowKey={(r) => r.species} emptyTitle="No active animal types yet."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <p className="text-card-title mb-2">Status</p>
              <ul className="flex flex-wrap gap-1.5">
                {s.statuses.map((x) => <li key={x} className="rounded-full border border-ink-300/30 px-2.5 py-0.5 text-helper-text text-ink-700">{x}</li>)}
              </ul>
            </div>
            <div>
              <p className="text-card-title mb-2">Source</p>
              <ul className="flex flex-wrap gap-1.5">
                {s.sources.map((x) => <li key={x} className="rounded-full border border-ink-300/30 px-2.5 py-0.5 text-helper-text text-ink-700">{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
    </ILMSCard>
  );
}

export default function CattleRegisterPage() {
  return (
    <TabbedPage
      title="Cattle Register"
      subtitle="Animals · Heat & AI · PD check · Calving dates · Milk yield · Vet visits — the farm's register book, sheet for sheet, from live portal records."
      tabs={[
        { id: 'dashboard', label: 'Dashboard', render: () => <DashboardSheet /> },
        { id: 'animals', label: 'Animal Register', render: () => <AnimalRegisterSheet /> },
        { id: 'breeding', label: 'Breeding & AI Log', render: () => <BreedingSheet /> },
        { id: 'calving', label: 'Calving Record', render: () => <CalvingSheet /> },
        { id: 'milk', label: 'Milk Yield Log', render: () => <MilkSheet /> },
        { id: 'vet', label: 'Vet Visit Record', render: () => <VetSheet /> },
        { id: 'settings', label: 'Settings & Lists', render: () => <SettingsSheet /> },
      ]}
    />
  );
}
